import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import RefreshIcon from "@mui/icons-material/Refresh";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MemoryIcon from "@mui/icons-material/Memory";
import StorageIcon from "@mui/icons-material/Storage";
import DnsIcon from "@mui/icons-material/Dns";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { api } from "../../mockData";

// SSL urgency thresholds (days remaining):
//   <0   = expired (red, "expired today/N days ago")
//   <7   = critical red
//   <30  = warning amber
//   else = healthy green
function sslColorFor(days) {
  if (days === null || days === undefined) return "#9e9e9e";
  if (days < 0) return "#dc2626";
  if (days < 7) return "#dc2626";
  if (days < 30) return "#f59e0b";
  return "#2e7d32";
}
function sslLabelFor(days) {
  if (days === null || days === undefined) return "—";
  if (days < 0) return `expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days === 0) return "expires today";
  return `${days} day${days === 1 ? "" : "s"} remaining`;
}

// Each ring (and its metric card below) reuses these brand-aligned colors.
// Picked to read OK on white surfaces in the admin dashboard.
const COLORS = {
  CPU: "#1976d2",
  Memory: "#9c27b0",
  Disk: "#2e7d32",
};
const ICONS = { CPU: DnsIcon, Memory: MemoryIcon, Disk: StorageIcon };
const TEXT = "#212121";
const SUBTLE = "#64748b";

// Inject the heartbeat keyframes once per page. Defined at module scope so
// repeated mounts don't recreate the <style> tag. Mirrors the pattern used
// in CallDistributionPie.jsx.
const KEYFRAMES_TAG_ID = "ss-combined-keyframes";
function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(KEYFRAMES_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = KEYFRAMES_TAG_ID;
  style.textContent = `
    @keyframes ssHeartbeat {
      0%, 100% { transform: scale(1); }
      14%      { transform: scale(1.18); }
      28%      { transform: scale(1); }
      42%      { transform: scale(1.13); }
      56%      { transform: scale(1); }
    }
    @keyframes ssGlow {
      0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.35); }
      50%      { box-shadow: 0 0 0 14px rgba(220, 38, 38, 0); }
    }
  `;
  document.head.appendChild(style);
}

// Format a "minutes ago" / "just now" relative timestamp for the
// "last refreshed" indicator. Stays under 30 chars for tight layouts.
function relativeTime(date) {
  if (!date) return "";
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

function ServerStatsCombined() {
  ensureKeyframes();
  const token = JSON.parse(localStorage.getItem("admin"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadedAt, setLoadedAt] = useState(null);

  // Auto-fetch on mount so the radar chart appears without a click. The
  // backend was changed to a non-blocking psutil sampling so this no longer
  // adds the ~1s delay it used to.
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await axios.get(`${api.dev}/api/serverstats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token?.access_token}`,
        },
      });
      setData(resp?.data?.data || null);
      setLoadedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load server stats");
    } finally {
      setLoading(false);
    }
  };

  const cpu = data?.cpu;
  const mem = data?.memory;
  const disk = data?.disk;

  // Nivo radial-bar wants series-of-points; we treat each metric as one
  // series with a single point (its current %). maxValue=100 keeps every
  // ring on the same 0-100% scale so the visual is comparable.
  const chartData = data
    ? [
        { id: "CPU",    data: [{ x: "Usage", y: Number(cpu?.usage_percent) || 0 }] },
        { id: "Memory", data: [{ x: "Usage", y: Number(mem?.usage_percent) || 0 }] },
        { id: "Disk",   data: [{ x: "Usage", y: Number(disk?.usage_percent) || 0 }] },
      ]
    : [];

  return (
    <Box
      className="serverstact"
      sx={{
        // Stretch to fill the parent flex slot in AdminDashboard so the
        // widget bottom-aligns with TopCarriersWidget regardless of which
        // one has more content.
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Header — chip on left, refresh + timestamp on right (only after load) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Chip
          icon={<MonitorHeartIcon style={{ color: "#fff" }} />}
          label="Server Monitoring"
          sx={{
            px: 1.2,
            py: 1,
            fontWeight: 600,
            background:
              "linear-gradient(135deg, #0a140e 0%, #0a140e 100%)",
            color: "#fff",
            fontSize: "0.85rem",
            letterSpacing: 0.5,
            "& .MuiChip-icon": { color: "#fff" },
          }}
        />

        {data && !loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: SUBTLE }}>
              Updated {relativeTime(loadedAt)}
            </Typography>
            <Tooltip title="Refresh server stats" arrow>
              <IconButton size="small" onClick={fetchStats} sx={{ color: TEXT }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Card
        sx={{
          p: 2,
          borderRadius: "20px",
          background: "#ffffffcc",
          backdropFilter: "blur(10px)",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
          minHeight: 340,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          justifyContent: "center",
        }}
      >
        {/* IDLE — pre-click state. The whole point of this component is to
            avoid auto-firing /api/serverstats (which blocks ~1s on the
            psutil sampling window). Show a clean call-to-action instead. */}
        {!data && !loading && !error && (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              // Soft radial halo behind the icon area — adds warmth without
              // overpowering the surrounding card.
              background:
                "radial-gradient(ellipse at center top, rgba(25,118,210,0.06) 0%, rgba(156,39,176,0.04) 35%, rgba(46,125,50,0.04) 65%, transparent 90%)",
              borderRadius: 2,
            }}
          >
            {/* Heartbeat icon — circular halo + scale pulse so the eye lands
                here first. Red to match the "monitor heart" semantic and
                signal "this is a vital-signs feature". */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 84,
                height: 84,
                borderRadius: "50%",
                mb: 1.5,
                background:
                  "radial-gradient(circle, rgba(220,38,38,0.18) 0%, rgba(220,38,38,0.04) 60%, transparent 100%)",
                animation: "ssGlow 2s ease-out infinite",
              }}
            >
              <MonitorHeartIcon
                sx={{
                  fontSize: 56,
                  color: "#dc2626",
                  filter: "drop-shadow(0 4px 6px rgba(220,38,38,0.35))",
                  animation: "ssHeartbeat 1.6s ease-in-out infinite",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: `${TEXT} !important`, mb: 0.5 }}>
              Server health snapshot
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: `${SUBTLE} !important`, mb: 2.5, px: 2 }}>
              CPU, memory and disk usage. Click below to fetch on demand —
              skipped on dashboard load to keep things snappy.
            </Typography>
            <Button
              variant="contained"
              onClick={fetchStats}
              startIcon={<MonitorHeartIcon />}
              sx={{
                textTransform: "none",
                // Tri-color gradient mirrors the three radial-bar rings
                // (CPU blue → Memory purple → Disk green) so the button
                // visually previews what the user is about to load.
                background:
                  "linear-gradient(135deg, #1976d2 0%, #9c27b0 50%, #2e7d32 100%)",
                color: "#fff !important",
                px: 3.5,
                py: 1.1,
                fontWeight: 700,
                fontSize: "0.92rem",
                letterSpacing: 0.3,
                boxShadow: "0 8px 22px rgba(124, 58, 237, 0.32)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565c0 0%, #7b1fa2 50%, #1b5e20 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 28px rgba(124, 58, 237, 0.42)",
                },
                "&:active": {
                  transform: "translateY(0)",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)",
                },
              }}
            >
              Load Server Stats
            </Button>
          </Box>
        )}

        {/* LOADING */}
        {loading && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: "#0a140e", mb: 2 }} />
            <Typography sx={{ fontSize: "0.85rem", color: SUBTLE }}>
              Loading server stats…
            </Typography>
          </Box>
        )}

        {/* ERROR */}
        {!loading && error && (
          <Box sx={{ py: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="outlined"
                onClick={fetchStats}
                startIcon={<RefreshIcon />}
                sx={{ textTransform: "none" }}
              >
                Try again
              </Button>
            </Box>
          </Box>
        )}

        {/* LOADED — radar chart on the LEFT, vertically-stacked metric
            details on the RIGHT. Saves vertical space vs the previous
            chart-on-top / details-below layout, and keeps the widget more
            horizontally-balanced inside its 50% dashboard slot. */}
        {!loading && data && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: 1.5,
              width: "100%",
            }}
          >
            {/* Chart column — radar on top, SSL Expiry tucked right under
                it (instead of as a Card-wide row at the bottom). Keeps the
                widget at its existing height — radar trimmed slightly to
                make room for SSL within the same vertical space. */}
            <Box
              sx={{
                flex: "2.1 1 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                minWidth: 0,
                width: "100%",
                gap: 0.5,
              }}
            >
              <Box sx={{ height: 250 }}>
                <ResponsiveRadialBar
                  data={chartData}
                  maxValue={100}
                  valueFormat={(v) => `${Math.round(v)}%`}
                  startAngle={-110}
                  endAngle={110}
                  innerRadius={0.35}
                  padding={0.35}
                  cornerRadius={6}
                  colors={(d) => COLORS[d.groupId] || "#9e9e9e"}
                  margin={{ top: 8, right: 12, bottom: 8, left: 12 }}
                  radialAxisStart={null}
                  circularAxisOuter={null}
                  enableTracks
                  tracksColor="rgba(0,0,0,0.05)"
                  // In-arc % labels disabled — at narrow arc angles they
                  // don't render cleanly. Hover tooltip already exposes
                  // the exact value, so the labels were redundant.
                  enableLabels={false}
                  tooltip={({ bar }) => (
                    <Box
                      sx={{
                        background: "#fff",
                        color: `${TEXT} !important`,
                        px: 1.5,
                        py: 0.9,
                        borderRadius: 1.2,
                        border: "1px solid #e5e7eb",
                        boxShadow: `0 8px 20px ${COLORS[bar.groupId]}55, 0 2px 8px rgba(0,0,0,0.18)`,
                        fontSize: "0.85rem",
                      }}
                    >
                      <strong style={{ color: COLORS[bar.groupId] }}>
                        {bar.groupId}
                      </strong>
                      : {Math.round(bar.value)}%
                    </Box>
                  )}
                  animate
                  motionConfig="gentle"
                />
              </Box>

              {/* SSL Expiry — sits directly under the radar so it consumes
                  the vertical slack created by the slightly shorter chart,
                  rather than expanding the whole Card downwards. */}
              {data?.ssl && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 0.6,
                    px: 0.5,
                  }}
                  title={data.ssl.hostname}
                >
                  {data.ssl.days_remaining < 0 ? (
                    <LockOpenIcon sx={{ fontSize: 17, color: "#c2410c", flexShrink: 0 }} />
                  ) : (
                    <LockIcon sx={{ fontSize: 17, color: "#c2410c", flexShrink: 0 }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: "#c2410c !important",
                      lineHeight: 1.3,
                    }}
                  >
                    <strong>SSL Expiry:</strong> {data.ssl.expires_at} (
                    {sslLabelFor(data.ssl.days_remaining)})
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Per-metric detail strip — single vertical column to the
                RIGHT of the radial chart. CPU, Memory, Disk stack one over
                the other so the widget can be narrower than its previous
                3-column-below layout. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 0.6,
                flex: "1 1 0",
                minWidth: 0,
                width: "100%",
              }}
            >
              <MetricRow
                title="CPU"
                pct={cpu?.usage_percent}
                color={COLORS.CPU}
                lines={[
                  `${cpu?.cores ?? "-"} cores`,
                  `Load ${
                    cpu?.load_average?.["1_min"] !== undefined
                      ? Number(cpu.load_average["1_min"]).toFixed(2)
                      : "-"
                  } [1m]`,
                ]}
              />
              <MetricRow
                title="Memory"
                pct={mem?.usage_percent}
                color={COLORS.Memory}
                lines={[
                  `${mem?.used_gb ?? "-"} / ${mem?.total_gb ?? "-"} GB used`,
                  `${mem?.available_gb ?? "-"} GB free`,
                ]}
              />
              <MetricRow
                title="Disk"
                pct={disk?.usage_percent}
                color={COLORS.Disk}
                lines={[
                  `${disk?.used_gb ?? "-"} / ${disk?.total_gb ?? "-"} GB used`,
                  `${disk?.free_gb ?? "-"} GB free`,
                ]}
              />
            </Box>
          </Box>
        )}

      </Card>
    </Box>
  );
}

function MetricRow({ title, pct, color, lines }) {
  const Icon = ICONS[title];
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.2,
        borderRadius: 2,
        background: `${color}10`,
        borderLeft: `3px solid ${color}`,
      }}
    >
      {Icon && (
        <Icon sx={{ color, fontSize: 28, flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: `${TEXT} !important` }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: `${color} !important` }}>
            {pct ?? 0}%
          </Typography>
        </Box>
        {lines.map((line, i) => (
          <Typography
            key={i}
            sx={{ fontSize: "0.75rem", color: `${SUBTLE} !important`, lineHeight: 1.3 }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default ServerStatsCombined;
